import { useEffect, useState } from "react";
import {
  ProjectCollaboratorsService,
  ProjectExtendedDetailService,
} from "shared/services/project";
import { handleErrors } from "shared/utils/helper";
import CustomBadge from "shared/components/common/customBadge";
import FeedbackRequestSkeleton from "shared/components/common/feedbackRequestSkeleton";
import classNames from "classnames";
import styles from "./style.module.scss";

interface ProjectUser {
  user_id: number;
  display_name?: string;
  email?: string;
  phone?: string;
  user_type?: string;
  designer_type?: string | null;
  profile_asset?: string | { path?: string } | null;
}

const SECTIONS: { title: string; types: string[] }[] = [
  { title: "TCHD Project Designers", types: ["designer"] },
  { title: "Client", types: ["client"] },
  { title: "Collaborators & Builders", types: ["collaborator", "builder"] },
];

interface ProjectCollaboratorsProps {
  projectId?: number;
}

const getInitials = (name?: string) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  return parts.length === 1
    ? parts[0][0].toUpperCase()
    : (parts[0][0] + parts[1][0]).toUpperCase();
};

const getAvatarUrl = (asset: ProjectUser["profile_asset"]) => {
  if (!asset) return null;
  if (typeof asset === "string") return asset;
  return asset.path || null;
};

const labelForDesignerType = (designerType?: string | null) => {
  const key = (designerType ?? "").toLowerCase();
  if (key === "both") return "Designer & Final Reviewer";
  if (key === "final_reviewer") return "Final Reviewer";
  return "Designer";
};

const labelForType = (type?: string, designerType?: string | null) => {
  if (type === "collaborator") return "Co-collaborator";
  if (type === "builder") return "Builder";
  if (type === "designer") return labelForDesignerType(designerType);
  if (!type) return "";
  return type.charAt(0).toUpperCase() + type.slice(1);
};

const ProjectCollaborators = ({ projectId }: ProjectCollaboratorsProps) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<ProjectUser[]>([]);

  useEffect(() => {
    if (!projectId) return;
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const [response, detailResponse] = await Promise.all([
          ProjectCollaboratorsService(projectId),
          ProjectExtendedDetailService(projectId),
        ]);
        const list =
          response?.data?.data ||
          response?.data?.collaborators ||
          response?.data ||
          [];
        const designers = detailResponse?.data?.data?.designers || [];
        // Designers come from the project detail endpoint; everyone else from collaborators.
        const designerRows: ProjectUser[] = (
          Array.isArray(designers) ? designers : []
        ).map((designer: any) => ({
          user_id: designer.user_id,
          display_name: designer.display_name,
          email: designer.email,
          phone: designer.phone,
          user_type: "designer",
          designer_type: designer.designer_type ?? null,
          profile_asset: designer.profile_asset?.path || null,
        }));
        setUsers([...designerRows, ...(Array.isArray(list) ? list : [])]);
      } catch (error) {
        handleErrors(error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [projectId]);

  if (loading) {
    return (
      <div className={styles.wrapper}>
        <FeedbackRequestSkeleton />
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.empty}>
          No one is linked to this project yet.
        </div>
      </div>
    );
  }

  const renderUser = (u: ProjectUser) => {
    const avatar = getAvatarUrl(u.profile_asset);
    return (
      <div key={u.user_id} className={styles.row}>
        <div className={styles.left}>
          {avatar ? (
            <img
              src={avatar}
              alt={u.display_name || ""}
              className={styles.avatar}
              onError={(e) => {
                e.currentTarget.style.display = "none";
                const fallback = e.currentTarget.nextElementSibling;
                if (fallback) {
                  (fallback as HTMLElement).style.display = "flex";
                }
              }}
            />
          ) : null}
          <div
            className={styles.initials}
            style={{ display: avatar ? "none" : "flex" }}
          >
            {getInitials(u.display_name)}
          </div>
          <div className={classNames("flex flex-col gap-1 min-w-0")}>
            <div className={styles.nameRow}>
              <h6 className={styles.name}>{u.display_name || "—"}</h6>
              {u.user_type && (
                <CustomBadge
                  textColor="#AD7636"
                  bg="#FDF6DD"
                  title={labelForType(u.user_type, u.designer_type)}
                />
              )}
            </div>
            {u.email && (
              <a className={styles.email} href={`mailto:${u.email}`}>
                {u.email}
              </a>
            )}
            {u.phone && (
              <a className={styles.phone} href={`tel:${u.phone}`}>
                {u.phone}
              </a>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.wrapper}>
      {SECTIONS.map(({ title, types }) => {
        const members = users.filter((u) =>
          types.includes(u.user_type || ""),
        );
        if (members.length === 0) return null;
        return (
          <div key={title}>
            <h5 className={styles.sectionTitle}>{title}</h5>
            <div className={styles.list}>{members.map(renderUser)}</div>
          </div>
        );
      })}
    </div>
  );
};

export default ProjectCollaborators;
